import { h, Component, Fragment, Prop, State } from '@stencil/core';
import state from '../../state';
import { updateStoredCustomField, getStoredFormData } from '../../utils/sessionStorage';

import type { CustomField, Screen } from '../../types';

// Hidden options must never render. console2 strips them on save, so live and
// test configs arrive clean, but its preview renders straight from editor state
// where they are still present — and filtering here also holds if an unstripped
// config ever reaches us some other way.
const visibleOptions = <T extends { isHidden?: boolean }>(options: T[] = []): T[] =>
  options.filter(option => !option.isHidden);

const inputFontStyle = (field: { inputFontFamily?: string; inputTextSize?: string }) => ({
  ...(field.inputFontFamily ? { fontFamily: field.inputFontFamily } : {}),
  ...(field.inputTextSize ? { fontSize: `${field.inputTextSize}px` } : {}),
});

// Radio kept its own (unrenamed) textFontFamily/textSize keys.
const radioFontStyle = (field: { textFontFamily?: string; textSize?: string }) => ({
  ...(field.textFontFamily ? { fontFamily: field.textFontFamily } : {}),
  ...(field.textSize ? { fontSize: `${field.textSize}px` } : {}),
});

@Component({
  tag: 'om-custom-fields',
  styleUrl: './custom-fields.css',
  scoped: true,
})
export class CustomFields {
  @Prop() customFields: CustomField[];
  @Prop() screen: Screen;

  // The one option selected in each radio field, keyed by field id, so that two
  // radio fields on the same screen stay independent. Single-select within a
  // field comes from the shared input name; this only records which option that
  // is, and whether there is one yet: the empty fallback input stays enabled
  // until there is, which keeps the payload shape identical to the dropdown's
  // (always exactly one entry for the field, empty when nothing is picked).
  @State() radioSelectionByField: Record<string, string> = {};

  defaultMonthLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  componentDidLoad() {
    // Restore date field values after component is fully loaded
    if (!state.miniOverlayConfig || state.miniOverlayConfig.shouldHide) {
      return;
    }

    const storedData = getStoredFormData(this.screen);
    if (!storedData?.customFields) return;

    // Restore date fields
    this.customFields?.forEach(field => {
      if (field.type === 'date') {
        const storedValue = storedData.customFields[field.id];
        if (storedValue) {
          const id = `om-overlay-custom-${field.id}`;
          const hiddenInput = state.shadowRoot.getElementById(id) as HTMLInputElement;

          if (hiddenInput) {
            // Parse stored date value (format: YYYY-MM-DD)
            const dateParts = storedValue.split('-');
            if (dateParts.length === 3) {
              const [yyyy, mm, dd] = dateParts;

              // Set dataset attributes
              hiddenInput.dataset.YYYY = yyyy;
              hiddenInput.dataset.MM = mm;
              hiddenInput.dataset.DD = dd;
              hiddenInput.value = storedValue;

              // Set visible input values
              // Find the container for THIS date field (previous sibling of hidden input)
              const container = hiddenInput.previousElementSibling;
              if (container) {
                const format = field.format || 'DD-MM-YYYY';
                format.split('-').forEach(fmt => {
                  if (fmt === 'DD' || fmt === 'YYYY') {
                    // Query inputs ONLY within this field's container
                    const visibleInputs = container.querySelectorAll('input.input.date-input-wrapper');
                    const targetInput = Array.from(visibleInputs).find(
                      (input: HTMLInputElement) => input.placeholder === fmt,
                    ) as HTMLInputElement;

                    if (targetInput) {
                      targetInput.value = fmt === 'DD' ? dd : yyyy;
                    }
                  }
                });
              }
            }
          }
        }
      }
    });
  }

  private isDateMatchFormate(input: HTMLInputElement) {
    return input.dataset.format.split('-').every(fmt => {
      const val = parseInt(input.dataset[fmt]);
      return input.dataset[fmt]?.length === fmt.length && !isNaN(val) && val > 0;
    });
  }

  private setInputDateValue(id: string) {
    const input = state.shadowRoot.getElementById(id) as HTMLInputElement;

    if (input && this.isDateMatchFormate(input)) {
      const { DD, MM, YYYY } = input.dataset;
      input.value = `${YYYY ?? new Date().getFullYear()}-${MM}-${DD}`;
    } else {
      input.value = '';
    }
  }

  private setInputDateError(id: string, fieldId: string) {
    const input = state.shadowRoot.getElementById(id) as HTMLInputElement;
    const { DD, MM, YYYY } = input.dataset;
    let error = { ...state.customFieldErrorMsg };
    if (DD?.length > 0 || MM?.length > 0 || YYYY?.length > 0) {
      if (input && this.isDateMatchFormate(input)) {
        delete error?.[fieldId];
      } else {
        error = { ...error, [fieldId]: 'Invalid date format' };
      }
    } else {
      delete error?.[fieldId];
    }
    state.customFieldErrorMsg = { ...error };
  }

  render() {
    if (!this.customFields) {
      return null;
    }

    let storedCustomFields: Record<string, string> = {};
    if (state.miniOverlayConfig && !state.miniOverlayConfig.shouldHide) {
      const storedData = getStoredFormData(this.screen);
      if (storedData?.customFields) {
        storedCustomFields = storedData.customFields;
      }
    }

    return this.customFields.map((field, index) => {
      const id = `om-overlay-custom-${field.id}`;
      const storedValue = storedCustomFields[field.id];

      switch (field.type) {
        case 'text':
          return (
            <om-clickable-element class="full-width" element={{ field: 'customFields', index }}>
              <input
                aria-label={field.placeholder}
                {...(state.mode === 'preview' ? { inert: true } : {})}
                class="input"
                type="text"
                name={field.id}
                placeholder={field.placeholder}
                style={{
                  ...(field.borderRadius ? { borderRadius: `${field.borderRadius}px` } : {}),
                  ...inputFontStyle(field),
                }}
                {...(storedValue && { value: storedValue })}
                onBlur={e => {
                  if (state.mode !== 'preview' && state.miniOverlayConfig && !state.miniOverlayConfig.shouldHide) {
                    const input = e.target as HTMLInputElement;
                    updateStoredCustomField(this.screen, field.id, input.value);
                  }
                }}
              />
            </om-clickable-element>
          );

        case 'hidden':
          return <input type="hidden" name={field.id} value={field.value} />;

        case 'dropdown': {
          return (
            <Fragment>
              <om-clickable-element class="full-width" element={{ field: 'customFields', index }}>
                <div ref={el => (state.dropdownContainerRefs[field.id] = el)}>
                  <om-dropdown-input
                    id={`om-overlay-${field.id}`}
                    placeholder={field.placeholder}
                    options={visibleOptions(field.options)}
                    borderRadius={field.borderRadius}
                    fontFamily={field.inputFontFamily}
                    fontSize={field.inputTextSize}
                    initialValue={storedValue}
                    handleSelect={(option: { value: string; label: string }) => {
                      const input = state.shadowRoot.getElementById(id) as HTMLInputElement;
                      if (input) {
                        input.value = option.value;
                      }

                      if (state.mode !== 'preview' && state.miniOverlayConfig && !state.miniOverlayConfig.shouldHide) {
                        updateStoredCustomField(this.screen, field.id, option.value);
                      }
                    }}
                    containerRefKey={field.id}
                  />
                </div>
              </om-clickable-element>
              <input id={id} type="hidden" name={field.id} value={storedValue || ''} />
            </Fragment>
          );
        }
        case 'date': {
          const options = (field.monthLabels ?? this.defaultMonthLabels).map((month, i) => ({
            label: month,
            value: (i + 1).toString().padStart(2, '0'),
          }));

          // Parse stored date value for month dropdown initialValue
          let initialMM = '';
          if (storedValue) {
            const dateParts = storedValue.split('-');
            if (dateParts.length === 3) {
              initialMM = dateParts[1];
            }
          }

          return (
            <Fragment>
              <om-clickable-element class="date-fields-container" element={{ field: 'customFields', index }}>
                {field.label && <div class="date-fields-label" innerHTML={field.label}></div>}
                <div class="date-fields-inputs">
                  {field.format.split('-').map(format => {
                    return format === 'MM' ? (
                      <div class="date-input-wrapper" ref={el => (state.dropdownContainerRefs[field.id] = el)}>
                        <om-dropdown-input
                          id={`om-overlay-${field.id}`}
                          placeholder={format}
                          options={options}
                          borderRadius={field.borderRadius}
                          fontFamily={field.inputFontFamily}
                          fontSize={field.inputTextSize}
                          initialValue={initialMM}
                          handleSelect={(option: { value: string }) => {
                            const input = state.shadowRoot.getElementById(id);
                            if (input) {
                              input.dataset.MM = option.value;
                            }
                            this.setInputDateValue(id);
                            this.setInputDateError(id, field.id);

                            // Save to session storage after updating
                            if (
                              state.mode !== 'preview' &&
                              state.miniOverlayConfig &&
                              !state.miniOverlayConfig.shouldHide
                            ) {
                              const hiddenInput = state.shadowRoot.getElementById(id) as HTMLInputElement;
                              if (hiddenInput && hiddenInput.value) {
                                updateStoredCustomField(this.screen, field.id, hiddenInput.value);
                              }
                            }
                          }}
                          containerRefKey={field.id}
                        />
                      </div>
                    ) : (
                      <input
                        class="input date-input-wrapper"
                        placeholder={format}
                        style={{
                          ...(field.borderRadius ? { borderRadius: `${field.borderRadius}px` } : {}),
                          ...inputFontStyle(field),
                        }}
                        {...(state.mode === 'preview' ? { inert: true } : {})}
                        onInput={e => {
                          const target = e.target as HTMLInputElement;
                          const input = state.shadowRoot.getElementById(id);

                          switch (true) {
                            case target.value === '':
                              input.dataset[format] = '';
                              break;

                            case format === 'DD' && /^\d{1,2}$/.test(target.value):
                              input.dataset.DD = target.value.padStart(2, '0');
                              break;

                            case format === 'YYYY' && /^[1-9]\d{0,3}$/.test(target.value):
                              input.dataset.YYYY = target.value;
                              break;

                            default:
                              target.value = input.dataset[format] ?? '';
                          }
                          this.setInputDateValue(id);
                        }}
                        onBlur={() => {
                          this.setInputDateError(id, field.id);

                          // Save to session storage after validation
                          if (
                            state.mode !== 'preview' &&
                            state.miniOverlayConfig &&
                            !state.miniOverlayConfig.shouldHide
                          ) {
                            const input = state.shadowRoot.getElementById(id) as HTMLInputElement;
                            if (input && input.value) {
                              updateStoredCustomField(this.screen, field.id, input.value);
                            }
                          }
                        }}
                      />
                    );
                  })}
                </div>
                {state.customFieldErrorMsg?.[field.id]?.length && state.showErrors && (
                  <span class={'error'}>{state.customFieldErrorMsg[field.id]}</span>
                )}
              </om-clickable-element>
              <input id={id} type="hidden" name={field.id} data-format={field.format} />
            </Fragment>
          );
        }

        case 'radio': {
          const selected = this.radioSelectionByField[field.id] ?? storedValue ?? '';
          const labelId = `${id}-label`;
          const options = visibleOptions(field.options);

          return (
            <Fragment>
              <om-clickable-element class="radio-fields-container" element={{ field: 'customFields', index }}>
                {field.label && <div class="radio-group-label" id={labelId} innerHTML={field.label}></div>}
                <div
                  class="radio-options"
                  role="radiogroup"
                  {...(field.label ? { 'aria-labelledby': labelId } : {})}
                  {...(state.mode === 'preview' ? { inert: true } : {})}
                  style={{
                    ...(field.textColour ? { color: field.textColour } : {}),
                    ...(field.radioColour ? { ['--om-radio-colour']: field.radioColour } : {}),
                  }}
                >
                  {options.map(option => (
                    <label class="radio-option" style={radioFontStyle(field)}>
                      <input
                        class="radio-input visually-hidden"
                        type="radio"
                        name={field.id}
                        value={option.value}
                        checked={selected === option.value}
                        onChange={() => {
                          this.radioSelectionByField = { ...this.radioSelectionByField, [field.id]: option.value };

                          if (
                            state.mode !== 'preview' &&
                            state.miniOverlayConfig &&
                            !state.miniOverlayConfig.shouldHide
                          ) {
                            updateStoredCustomField(this.screen, field.id, option.value);
                          }
                        }}
                      />
                      <span class="radio-control" aria-hidden="true"></span>
                      <span class="radio-label">{option.label}</span>
                    </label>
                  ))}
                </div>
              </om-clickable-element>
              <input id={id} type="hidden" name={field.id} value="" disabled={!!selected} />
            </Fragment>
          );
        }

        default:
          return null;
      }
    });
  }
}
